import { useState } from 'react';
import { useRoute, Link } from 'wouter';
import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, PageTitle } from '../../components/admin/ui';
import { formatBRLCompact, ORIGEM_LABEL, PLANO_LABEL } from '../../lib/format';

export function AdminLeadDetalhe() {
  const [, params] = useRoute('/admin/leads/:id');
  const id = params?.id ?? '';
  const { data, loading, error, reload } = useAsync(() => trpc.admin.lead.query({ id }), [id]);
  const [aviso, setAviso] = useState<string | null>(null);
  const placeholder = (a: string) =>
    setAviso(`"${a}": ação real vem na próxima fase (esta etapa é só estrutura + dados).`);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-2">
        <Link href="/admin/leads" className="text-sm text-ink-3 hover:text-ink">
          ← Leads / CRM
        </Link>
      </div>

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}
      {data === null && !loading && <ErrorBox msg="Lead não encontrado." />}

      {data && (
        <>
          <PageTitle titulo={data.lead.nome} />

          <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            <div className="space-y-5">
              <div className="card p-5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Campo label="Contato" valor={data.lead.contato ?? '-'} />
                  <Campo label="Origem" valor={ORIGEM_LABEL[data.lead.origem] ?? data.lead.origem} />
                  <Campo label="Porte estimado" valor={`${data.lead.porteEstimado} contratos`} />
                  <Campo label="Plano provável" valor={data.lead.planoProvavel ? (PLANO_LABEL[data.lead.planoProvavel] ?? '-') : '-'} />
                  <Campo label="MRR potencial" valor={formatBRLCompact(data.lead.mrrPotencial)} destaque />
                  <Campo label="Último toque" valor={data.lead.ultimoToque ?? '-'} />
                </div>
                {data.lead.proximoPasso && (
                  <div className="mt-4 rounded-sm border border-line px-4 py-3 text-sm">
                    <span className="text-xs uppercase tracking-wide text-ink-4">Próximo passo</span>
                    <div className="mt-1 text-ink-2">{data.lead.proximoPasso}</div>
                  </div>
                )}
                {data.lead.notas && <div className="mt-3 text-sm text-ink-3">{data.lead.notas}</div>}
              </div>

              <div className="card p-5">
                <div className="text-[11px] uppercase tracking-wide text-ink-4">Histórico de interações</div>
                <ul className="mt-3 space-y-3">
                  {data.interacoes.length === 0 && <li className="text-sm text-ink-4">Sem interações registradas.</li>}
                  {data.interacoes.map((it, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="num shrink-0 text-ink-4">
                        {new Date(it.data).toLocaleDateString('pt-BR')}
                      </span>
                      <span className="text-ink-2">
                        <span className="font-medium capitalize text-ink">{it.tipo}</span>: {it.descricao}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="card h-fit p-5">
              <div className="text-[11px] uppercase tracking-wide text-ink-4">Ações</div>
              <div className="mt-3 space-y-2">
                <BotaoAcao label="Mover de estágio" onClick={() => placeholder('Mover de estágio')} />
                <BotaoAcao label="Agendar follow-up" onClick={() => placeholder('Agendar follow-up')} />
                <BotaoAcao label="Adicionar nota" onClick={() => placeholder('Adicionar nota')} />
              </div>
              {aviso && (
                <div className="mt-3 rounded-sm bg-bg-warm px-3 py-2 text-xs text-ink-3">{aviso}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Campo({ label, valor, destaque }: { label: string; valor: string; destaque?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-4">{label}</div>
      <div className="num mt-0.5 text-sm font-medium" style={{ color: destaque ? 'var(--accent)' : 'var(--ink)' }}>
        {valor}
      </div>
    </div>
  );
}

function BotaoAcao({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-sm border border-line-2 px-4 py-2.5 text-left text-sm text-ink transition-colors hover:bg-bg-warm"
    >
      {label} <span className="text-ink-3">→</span>
    </button>
  );
}
